import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useSettings from "../../data/useSettings";
import AdminSettingsTable from "../../components/adminSettings/AdminSettingsTable";
import BusinessCurrencyCard from "../../components/adminSettings/BusinessCurrencyCard";
import TvLocationTilesCard from "../../components/adminSettings/TvLocationTilesCard";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { GENERAL_SETTINGS_TAB, groupSettingsByPrefix, settingTabLabel } from "./settingTabs";

/**
 * The raw key/value settings, one prefix at a time. Which prefix comes from `?tab=`, which the
 * admin landing page and the strip above this panel link to; without one it opens on the first.
 */
export default function AdminCredentials() {
  const { setInfo } = usePageNameContext();
  const { generalSettings, loading } = useSettings();
  const hasProductionManager = CheckFeatureFlag("Production Manager");
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");

  const { tabs, grouped } = useMemo(() => groupSettingsByPrefix(generalSettings), [generalSettings]);

  const activeTab = requestedTab && tabs.includes(requestedTab) ? requestedTab : tabs[0];

  useEffect(() => {
    setInfo({
      name: "Admin Settings",
      desc: "View and configure your admin settings",
      fill: false,
      nameSuffix: activeTab ? settingTabLabel(activeTab) : "",
    });
  }, [setInfo, activeTab]);

  if (loading) {
    return (
      <div className="p-6">
        <LoadingWheel />
      </div>
    );
  }

  if (!activeTab) {
    return <p className="text-sm text-gray-500">There are no admin settings for this account yet.</p>;
  }

  // The General tab is hidden when it has no rows, so the cards fall back to the first tab rather
  // than disappearing on a customer with only prefixed settings.
  const showGeneralCards = tabs.includes(GENERAL_SETTINGS_TAB)
    ? activeTab === GENERAL_SETTINGS_TAB
    : activeTab === tabs[0];

  return (
    <div>
      {showGeneralCards && <BusinessCurrencyCard />}
      {showGeneralCards && hasProductionManager && <TvLocationTilesCard />}
      <AdminSettingsTable settings={grouped[activeTab] ?? []} />
    </div>
  );
}
